import React, { useEffect, useState, useContext } from 'react'
import { observer } from 'mobx-react-lite'
import { ThreeDotsVertical, Instagram, Tiktok, Pinterest, Youtube, Linkedin, Headphones, Stars, Person } from 'react-bootstrap-icons'
import { useNavigate, useParams } from 'react-router-dom'
import axiosService from '../../../utils/AxiosService'
import EditOptions from './EditOptions'
import ArchiveModal from './ArchiveModal'
import { toastSuccess } from '../../FormComponents/Toasts'
import { BrowseContext } from '../../../contexts/BrowseContext'

export default observer(function CampaignsListItem({ campaign }) {
  const { browseState, clientCampaigns } = useContext(BrowseContext)
  const navigate = useNavigate()
  const { client_id, folder_id } = useParams()
  const [collaborators, setCollaborators] = useState([])
  const [openEdit, setOpenEdit] = useState(false)
  const [archiveModalOpen, setArchiveModalOpen] = useState(false)

  const getCollaborators = () => {
    axiosService.get(`/api/project_manager/campaigns/${campaign.id}/collaborators`)
    .then(collabs => setCollaborators(collabs)).catch(error => console.log(error))
  }

  useEffect(() => getCollaborators(), [])

  return (
    <div
      className='h-[144px] border-1 border-rb-lighter-grey dark:border-rb-dark-mode-lighter rounded-lg bg-white dark:bg-rb-dark-mode flex flex-col justify-between p-3 hover:cursor-pointer'
      onClick={() => navigate(`/project_manager/campaigns/${campaign.id}/overview`)}
    >
      <div className="flex items-center justify-between">
        <div className='flex items-center'>
          <span className='h-10 w-10 rounded-lg text-lg flex items-center justify-center' style={{ backgroundColor: campaign.theme_color }}>
            <span className='text-white'>
              {campaign.name.slice(0, 2).toUpperCase()}
            </span>
          </span>
          <div className='pl-3'>
            <p className='rb-text-muted text-sm'>{campaign.client_name}</p>
            <h4 className='text-md font-bold dark:text-white truncate w-full'>{campaign.name}</h4>
          </div>
        </div>
        <span className='relative'>
          <button
            type='button'
            className='h-10 w-10 rounded-lg hover:bg-rb-grey dark:hover:bg-rb-dark-mode-lighter flex justify-center items-center dark:text-white'
            onClick={(e) => {
              e.stopPropagation()
              setOpenEdit(!openEdit)
            }}
          >
            <ThreeDotsVertical />
          </button>
          <EditOptions
            isOpen={openEdit}
            onClose={() => {
              browseState.selectedCampaignFolderId = campaign?.folder_id || null
              browseState.selectedCampaignId = campaign.id
              setOpenEdit(false)
            }}
            onDelete={() => {
              axiosService.delete(`/api/project_manager/campaigns/${campaign.id}`)
                .then((removedCampaign) => {
                  browseState.clientCampaigns = clientCampaigns.filter(campaign => campaign.id !== removedCampaign.id)
                  folder_id ?
                  navigate(`/project_manager/campaigns/clients/${removedCampaign.client_id}/folders/${folder_id}`) :
                  navigate(`/project_manager/campaigns/clients/${removedCampaign.client_id}`)

                  toastSuccess(`${removedCampaign.name} has been deleted!`)
                })
                .catch(err => console.log(err))
            }}
            onArchive={() => setArchiveModalOpen(true)}
            isCampaign
          />
        </span>
        <span onClick={e => e.stopPropagation()}>
          <ArchiveModal
            isOpen={archiveModalOpen}
            onClose={() => setArchiveModalOpen(false)}
            name={campaign.name}
            onConfirm={() => browseState.archiveCampaign(campaign)}
          />
        </span>
      </div>

      <div className='flex items-center justify-between'>
        <div className="flex pl-2">
          {collaborators.map(collaborator => (
            <div key={collaborator.id} className='-ml-2'>
              {collaborator.avatar?.url && <img className="w-7 h-7 rounded-full object-cover" src={collaborator.avatar?.url} alt="avatar" />
                || <div className="w-7 h-7 bg-rb-green rounded-full text-white flex items-center justify-center border-1 border-white"><Person /></div>}
            </div>
          ))}
        </div>
        <div className='flex items-center'>
          {campaign.platforms.includes('instagram') && <span className='w-6 h-6 ml-2 rounded-full bg-rb-grey dark:bg-rb-dark-mode-lighter text-black dark:text-pink-400 flex justify-center items-center'><Instagram /></span>}
          {campaign.platforms.includes('tiktok') && <span className='w-6 h-6 ml-2 rounded-full bg-rb-grey dark:bg-rb-dark-mode-lighter text-black dark:text-white flex justify-center items-center'><Tiktok /></span>}
          {campaign.platforms.includes('youtube') && <span className='w-6 h-6 ml-2 rounded-full bg-rb-grey dark:bg-rb-dark-mode-lighter text-black dark:text-red-400 flex justify-center items-center'><Youtube /></span>}
          {campaign.platforms.includes('pinterest') && <span className='w-6 h-6 ml-2 rounded-full bg-rb-grey dark:bg-rb-dark-mode-lighter text-black dark:text-white flex justify-center items-center'><Pinterest /></span>}
          {campaign.platforms.includes('linkedin') && <span className='w-6 h-6 ml-2 rounded-full bg-rb-grey dark:bg-rb-dark-mode-lighter text-black dark:text-rb-linkedin flex justify-center items-center'><Linkedin /></span>}
          {campaign.platforms.includes('podcast') && <span className='w-6 h-6 ml-2 rounded-full bg-rb-grey dark:bg-rb-dark-mode-lighter text-black dark:text-white flex justify-center items-center'><Headphones /></span>}
          {campaign.platforms.includes('other') && <span className='w-6 h-6 ml-2 rounded-full bg-rb-grey dark:bg-rb-dark-mode-lighter text-black dark:text-amber-500 flex justify-center items-center'><Stars /></span>}
        </div>
      </div>

      <div className='flex justify-between items-center'>
        <div>
          <span className={`px-2 py-1 rounded-full ${campaign.is_finished ? 'bg-rb-lite-grey dark:bg-rb-dark-mode-lighter rb-text-secondary' : 'bg-rb-soft-green dark:bg-rb-green/20 text-rb-green dark:text-rb-green'}`}>
            {campaign.is_finished ? 'Finished' : 'Active'}
          </span>
          <span className='rb-text-muted text-sm ml-4'>
            {campaign.created_at.utc.split(' ')[0]}
          </span>
        </div>
        <span>
          {
            campaign.project_number && (
              <span className='px-2 py-1 rounded-full bg-rb-grey dark:bg-rb-dark-mode-lighter dark:text-white'>
                {campaign.project_number}
              </span>
            )
          }
        </span>
      </div>
    </div>
  )
})
